import { useMemo } from "react";
import { AgGrid } from "@jsq/ds/core";
import { useGpPositionDetailsForEntityLazyQuery } from "../../../../graphql/supergraphHooks";
import { prepareDataForDisplay } from "../utils";
import type { CompliancePositionSort } from "../../../../graphql/supergraphTypes";

export const POSITION_PAGE_SIZE = 20;

type SetFilterModel = { filterType: "set"; values: string[] | null };

export function usePositionDatasource(
  arenaId: number,
  entityId: number,
): AgGrid.IServerSideDatasource {
  const [executePositions] = useGpPositionDetailsForEntityLazyQuery();

  return useMemo(
    (): AgGrid.IServerSideDatasource => ({
      getRows: (params: AgGrid.IServerSideGetRowsParams) => {
        const { startRow = 0 } = params.request;
        const page = Math.floor(startRow / POSITION_PAGE_SIZE);

        const sortModel = params.request.sortModel.find((s) => s.colId === "investorName");
        const sort: CompliancePositionSort | undefined = sortModel
          ? { field: "POSITION_NAME", sortAscending: sortModel.sort === "asc" }
          : undefined;

        const filterModel = params.request.filterModel as Record<string, SetFilterModel> | null;
        const positionFilterValues = filterModel?.["investorName"]?.values;
        const filteredIds = positionFilterValues?.map((id) => Number(id)) ?? [];
        const positionIds = filteredIds?.length ? filteredIds : undefined;

        void (async () => {
          try {
            const result = await executePositions({
              variables: { arenaId, entityId, page, sort, positionIds },
              fetchPolicy: "network-only",
            });
            const positions = result.data?.compliancePositions;
            if (!positions) {
              params.fail();
              return;
            }
            const rowData = prepareDataForDisplay(positions);
            // When fewer rows than page size come back we've reached the last page;
            // passing rowCount tells AG Grid the exact total so pagination stops.
            const rowCount =
              positions.length < POSITION_PAGE_SIZE ? startRow + positions.length : undefined;
            params.success({ rowData, rowCount });
          } catch {
            params.fail();
          }
        })();
      },
    }),
    [executePositions, arenaId, entityId],
  );
}
